/**
 * What is true about this account no matter which tab you are on.
 *
 * Gap A phase 2c. These three blocks rendered inside the legacy Management
 * body, which is under the category bar — so they repeated beneath all seven
 * tabs, and switching tabs changed one node out of fifteen ("whichever tab
 * you're on, the page looks almost identical once you scroll past the top",
 * Art 2026-09-22).
 *
 * None of them is a tool. Each is a caveat about the ACCOUNT: the verdict
 * line, whether writes are switched off, and the spend this screen cannot
 * see. A caveat that applies to every tab belongs above the tabs, said once,
 * so the area under the bar is the tab body, the drill-down and change
 * history — nothing else.
 *
 * Reads come from the phase-2 hooks rather than props: they keep the exact
 * query keys the legacy body used, so react-query dedupes and this costs no
 * extra network trip.
 */
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';

import {
  canWrite,
  type AdMonitorResponse,
  type ConversionViewResponse,
} from '@/services/googleAdsManagementService';

import {
  useManagementAds,
  useManagementConversions,
} from './useManagementReads';
import { useManagementWindowDays } from './useManagementWindow';

/**
 * The one sentence a human would say out loud about this account.
 *
 * ⛔ BUILT ONLY FROM READS THAT SUCCEEDED. Every clause is gated on its own
 * `read_ok`, so a failed read can never contribute a reassuring phrase. If
 * nothing could be read the verdict says so rather than saying "all clear",
 * which is the failure that told a client they had zero spend in a month they
 * spent $3,953.
 *
 * Viktor: "each page should open with the one sentence a human would say out
 * loud; the table is the evidence."
 */
function verdictLine(
  days: number,
  ads?: AdMonitorResponse,
  conversions?: ConversionViewResponse,
): { text: string; tone: 'bad' | 'ok' | 'unknown' } {
  const readAny = ads?.read_ok || conversions?.read_ok;
  if (!readAny) {
    return {
      text: 'Nothing could be read from Google Ads for this account, so none of the numbers below are findings.',
      tone: 'unknown',
    };
  }

  const findings: string[] = [];

  // Ordered by what costs money soonest, not by how easy it is to say.
  if (conversions?.read_ok && (conversions.gbp_primary_count ?? 0) > 0) {
    findings.push(
      `${conversions.gbp_primary_count} Google Business Profile action(s) are counted as leads, so the conversion figures below are inflated`,
    );
  }
  if (conversions?.read_ok && (conversions.silent_count ?? 0) > 0) {
    findings.push(
      `${conversions.silent_count} primary conversion action(s) have recorded nothing in 29 days, and bidding is still optimising toward them`,
    );
  }
  if (ads?.read_ok && (ads.waste_count ?? 0) > 0) {
    findings.push(
      `${ads.waste_count} ad(s) spent $${Math.round(ads.waste_spend ?? 0).toLocaleString()} and converted nothing`,
    );
  }
  if (ads?.read_ok && (ads.dark_ad_groups?.length ?? 0) > 0) {
    findings.push(
      `${ads.dark_ad_groups.length} ad group(s) have live keywords and nothing serving`,
    );
  }
  if (ads?.read_ok && (ads.disapproved_count ?? 0) > 0) {
    findings.push(`${ads.disapproved_count} ad(s) are disapproved`);
  }

  if (findings.length === 0) {
    // ⚠ Name the universe even when clean. "Nothing to act on" is only
    // meaningful if the reader knows what was looked at.
    return {
      text: `Nothing to act on: ${ads?.ads.length ?? 0} ad(s) and ${conversions?.actions?.length ?? 0} conversion action(s) checked over the last ${days} days.`,
      tone: 'ok',
    };
  }
  const head = findings[0];
  const rest = findings.slice(1);
  return {
    text: rest.length
      ? `${head[0].toUpperCase()}${head.slice(1)}. Also: ${rest.join('; ')}.`
      : `${head[0].toUpperCase()}${head.slice(1)}.`,
    tone: 'bad',
  };
}

function VerdictBanner({
  days,
  ads,
  conversions,
}: {
  days: number;
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
}) {
  const { text, tone } = verdictLine(days, ads, conversions);
  const styles =
    tone === 'bad'
      ? 'border-destructive/40 bg-destructive/5'
      : tone === 'unknown'
        ? 'border-coral/40 bg-coral-light/20'
        : 'border-border';
  const Icon =
    tone === 'bad' ? TriangleAlert : tone === 'unknown' ? AlertCircle : CheckCircle2;
  return (
    <div className={`flex items-start gap-2 rounded-md border p-3 ${styles}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="text-sm font-medium">{text}</p>
    </div>
  );
}

export function ManagementAccountCaveats({ tenantId }: { tenantId: string }) {
  const ads = useManagementAds(tenantId);
  const conversions = useManagementConversions(tenantId);
  // The same window the two reads above were fetched for, so the verdict's
  // "checked over the last N days" cannot disagree with the selector.
  const days = useManagementWindowDays();

  return (
    <div className="space-y-4">
      <VerdictBanner days={days} ads={ads.data} conversions={conversions.data} />
      {/* ⛔ A STATEMENT, NOT AN AFFORDANCE.
          Pause/enable used to render with the flag off and 503 on click. Hiding
          the buttons fixed the lie but introduced a quieter one: an operator
          could no longer tell the capability EXISTS, so they would never think
          to ask for it. Viktor's answer, and the right one — say it in words.
          Nothing here is clickable, so nothing can pretend to work; the reader
          learns what is off and what to do instead.
          Rendered only once a read has actually answered, so a loading screen
          never claims writes are off. */}
      {ads.data && !canWrite(ads.data.capabilities, 'ad_status_write') && (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Write actions (pause and enable) are switched off for this account,
            so those controls are not shown. Use{' '}
            <span className="font-medium">Open in Google Ads</span> on any row to
            make the change by hand.
          </p>
        </div>
      )}
      {/* ⛔ SAY WHAT THIS SCREEN CANNOT SEE. Ad health reads ad_group_ad;
          Performance Max serves from asset groups, so a live PMax campaign is
          structurally absent from every number above. The tiles honestly say
          "of 55 ads" — nothing said those 55 exclude a whole campaign type,
          and an unlabelled omission reads as coverage.
          Stated only when the account actually runs PMax, and with the spend
          attached: a disclaimer is ignorable, "$6,377 you cannot see here" is
          not. */}
      {ads.data?.read_ok && (ads.data.uncovered_pmax_campaigns ?? 0) > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-coral/40 bg-coral-light/20 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-coral-dark" />
          <p className="text-sm">
            <span className="font-medium">
              {ads.data.uncovered_pmax_campaigns} Performance Max campaign
              {ads.data.uncovered_pmax_campaigns === 1 ? '' : 's'} (
              ${Math.round(ads.data.uncovered_pmax_spend ?? 0).toLocaleString()} in
              the last {ads.data.window_days ?? 30} days) {ads.data.uncovered_pmax_campaigns === 1 ? 'is' : 'are'} not
              covered by this screen.
            </span>{' '}
            Performance Max serves from asset groups rather than ads, so none of
            its spend or conversions appear in Ad health. The Performance Max
            section below shows where that money actually went.
          </p>
        </div>
      )}
    </div>
  );
}

export default ManagementAccountCaveats;
